
import './App.css';
import React from 'react';
import BibleExplorer from './BibleExplorer';
import InfoPage from './InfoPage';
import SettingsPage from './SettingsPage';

type Category = 'bible' | 'info' | 'settings';

interface AppSettings {
  fontSize: 'small' | 'medium' | 'large';
  theme: 'dark' | 'light';
  defaultVersion: string;
  accessibilityMode?: boolean;
}

function App() {
  const [category, setCategory] = React.useState<Category>('bible');
  const [settings, setSettings] = React.useState<AppSettings>({
    fontSize: 'medium',
    theme: 'dark',
    defaultVersion: 'KJV',
    accessibilityMode: false
  });

  React.useEffect(() => {
    // Load settings from localStorage
    const saved = localStorage.getItem('verlume_settings');
    if (saved) {
      try {
        const loaded = JSON.parse(saved);
        setSettings(loaded);
        // Apply theme to document
        applyTheme(loaded.theme, loaded.fontSize, loaded.accessibilityMode);
      } catch (e) {
        console.error('Failed to load settings');
      }
    }
  }, []);

  const applyTheme = (theme: 'dark' | 'light', fontSize: 'small' | 'medium' | 'large', accessibility: boolean = false) => {
    const root = document.documentElement;
    
    // Apply accessibility mode with softer high contrast (easier on sensitive eyes)
    if (accessibility) {
      if (theme === 'light') {
        root.style.setProperty('--bg-primary', '#FAFAFA');
        root.style.setProperty('--bg-secondary', '#F0F0F0');
        root.style.setProperty('--bg-tertiary', '#E8E8E8');
        root.style.setProperty('--text-primary', '#2A2A2A');
        root.style.setProperty('--text-secondary', '#2A2A2A');
        root.style.setProperty('--text-tertiary', '#2A2A2A');
        root.style.setProperty('--border-color', '#2A2A2A');
        root.style.setProperty('--gold', '#2A2A2A');
        root.style.setProperty('--shadow', '0 2px 8px rgba(0, 0, 0, 0.25)');
      } else {
        root.style.setProperty('--bg-primary', '#0A0A0A');
        root.style.setProperty('--bg-secondary', '#1A1A1A');
        root.style.setProperty('--bg-tertiary', '#2D2D2D');
        root.style.setProperty('--text-primary', '#DA70D6');
        root.style.setProperty('--text-secondary', '#DA70D6');
        root.style.setProperty('--text-tertiary', '#DA70D6');
        root.style.setProperty('--border-color', '#DA70D6');
        root.style.setProperty('--gold', '#DA70D6');
        root.style.setProperty('--shadow', '0 2px 8px rgba(218, 112, 214, 0.25)');
      }
      // Add accessibility mode attribute
      root.setAttribute('data-accessibility-mode', 'true');
    } else {
      root.removeAttribute('data-accessibility-mode');
      if (theme === 'light') {
        root.style.setProperty('--bg-primary', '#FFFFFF');
        root.style.setProperty('--bg-secondary', '#F5F5F5');
        root.style.setProperty('--bg-tertiary', '#EEEEEE');
        root.style.setProperty('--text-primary', '#1A1A1A');
        root.style.setProperty('--text-secondary', '#555555');
        root.style.setProperty('--text-tertiary', '#9D4EDD');
        root.style.setProperty('--border-color', '#9D4EDD');
        root.style.setProperty('--gold', '#9D4EDD');
        root.style.setProperty('--shadow', '0 4px 16px rgba(0, 0, 0, 0.15)');
      } else {
        root.style.setProperty('--bg-primary', '#0A0E27');
        root.style.setProperty('--bg-secondary', '#11152E');
        root.style.setProperty('--bg-tertiary', '#1A2347');
        root.style.setProperty('--text-primary', '#F5F7FA');
        root.style.setProperty('--text-secondary', '#B0B5C1');
        root.style.setProperty('--text-tertiary', '#9D4EDD');
        root.style.setProperty('--border-color', '#9D4EDD');
        root.style.setProperty('--gold', '#9D4EDD');
        root.style.setProperty('--shadow', '0 4px 16px rgba(157, 78, 221, 0.15)');
      }
    }
    
    // Apply font size (larger in accessibility mode)
    const fontSizeMap = accessibility 
      ? { small: '18px', medium: '20px', large: '22px' }
      : { small: '14px', medium: '16px', large: '18px' };
    root.style.setProperty('--base-font-size', fontSizeMap[fontSize]);
    document.documentElement.style.fontSize = fontSizeMap[fontSize];
  };

  const handleSettingsUpdate = (newSettings: AppSettings) => {
    setSettings(newSettings);
    applyTheme(newSettings.theme, newSettings.fontSize, newSettings.accessibilityMode);
  };

  const handleCategoryChange = (newCategory: Category) => {
    setCategory(newCategory);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--bg-primary)', color: 'var(--text-primary)', transition: 'background 0.3s, color 0.3s' }}>
      {/* Category Navigation (visible on all pages except info) */}
      {category !== 'info' && (
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          padding: '0.8rem 1rem',
          background: '#16161B',
          borderBottom: '1px solid rgba(255,215,0,0.15)',
          justifyContent: 'center',
          flexWrap: 'wrap',
          minHeight: '54px'
        }}>
          <button
            onClick={() => handleCategoryChange('bible')}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: category === 'bible' ? '2px solid #9D4EDD' : '1px solid #555',
              background: category === 'bible' ? 'rgba(157,78,221,0.15)' : 'transparent',
              color: category === 'bible' ? '#9D4EDD' : '#ccc',
              cursor: 'pointer',
              fontWeight: 600,
              transition: 'all 0.2s',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.95rem'
            }}
            title="Bible Scriptures"
          >
            <span style={{display:'flex',alignItems:'center',gap:'0.4rem'}}><span className="material-icons" style={{fontSize:'1.2rem'}}>library_books</span> Bible</span>
          </button>
          <button
            onClick={() => handleCategoryChange('settings')}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: category === 'settings' ? '2px solid #9D4EDD' : '1px solid #555',
              background: category === 'settings' ? 'rgba(157,78,221,0.15)' : 'transparent',
              color: category === 'settings' ? '#9D4EDD' : '#ccc',
              cursor: 'pointer',
              fontWeight: 600,
              transition: 'all 0.2s',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.95rem'
            }}
            title="Settings"
          >
            <span style={{display:'flex',alignItems:'center',gap:'0.4rem'}}><span className="material-icons" style={{fontSize:'1.2rem'}}>settings</span> Settings</span>
          </button>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          div[style*="border-bottom: 1px solid rgba(255,215,0,0.15)"] {
            padding: 0.6rem 0.8rem !important;
            gap: 0.3rem !important;
          }
          div[style*="border-bottom: 1px solid rgba(255,215,0,0.15)"] button {
            padding: 0.4rem 0.7rem !important;
            font-size: 0.85rem !important;
          }
        }
        @media (max-width: 480px) {
          div[style*="border-bottom: 1px solid rgba(255,215,0,0.15)"] {
            padding: 0.5rem 0.6rem !important;
            gap: 0.2rem !important;
            minHeight: 48px !important;
          }
          div[style*="border-bottom: 1px solid rgba(255,215,0,0.15)"] button {
            padding: 0.35rem 0.6rem !important;
            font-size: 0.8rem !important;
            white-space: nowrap;
          }
        }
      `}</style>

      {/* Main Content */}
      <div style={{ flex: 1 }}>
        {category === 'bible' && (
          <BibleExplorer onInfo={() => handleCategoryChange('info')} defaultVersion={settings.defaultVersion} />
        )}
        {category === 'settings' && (
          <SettingsPage onBack={() => handleCategoryChange('bible')} currentSettings={settings} onSettingsChange={handleSettingsUpdate} />
        )}
        {category === 'info' && (
          <InfoPage onBack={() => handleCategoryChange('bible')} />
        )}
      </div>
    </div>
  );
}

export default App;
